import { debounce, findLast, get, set, update } from 'lodash';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { INFOBOX_STATUS } from '@hivebrite/design-system/Infobox';
import { useIsMounted, useRefFn } from '@hivebrite/react-hooks';

import { createNewItem } from 'hb-builder/utils/createNewItem';
import { getBodyItemInDocument } from 'hb-builder/utils/getBodyItemInDocument';
import { getClosestValue } from 'hb-builder/utils/getClosestValue';
import { assignPath } from 'hb-builder/utils/item';
import { BUILDER_DOCUMENT_GLOBAL_TYPE } from 'hb-react/backoffice/types/builder/builderDocument';

const UNDRAGGABLE_COMPONENTS = [
  'EmailFooter',
  'EmailMirrorLink',
  'EmailActivationButton',
];

const ERRORS = {
  document_almost_too_big: {
    errorType: INFOBOX_STATUS.INFO,
    message: s__(
      'Builder|Your custom page is nearing the maximum allowed limit of 4,000,000 characters. Consider reducing the amount of text, code, or embedded content to avoid potential issues.',
    ),
  },
  document_too_big: {
    errorType: INFOBOX_STATUS.ERROR,
    message: s__(
      'Builder|Your custom page cannot be saved because it exceeds the allowed limit of 4,000,000 characters for the entire page. Reduce the amount of text, code, or embedded content to proceed.',
    ),
  },
  unsupported_images: {
    errorType: INFOBOX_STATUS.ERROR,
    message: s__(
      'Builder| We’ve detected one or more images in Base64/Data Image format within your custom page content. Please remove or replace these images with external links to save your page.',
    ),
  },
};

const BuilderContext = createContext();

// https://hivebrite.atlassian.net/browse/AJ-4949
const WEIGHT_LIMITATION = 4e6;

export const BuilderProvider = ({
  children,
  flags,
  initialDocument,
  onChange,
}) => {
  const [changeCount, setChangeCount] = useState(0);
  const [error, setError] = useState();
  const [fullDocument, setFullDocument] = useState(
    createNewItem(initialDocument, { isDuplicate: true }),
  );
  const [isResizing, setIsResizing] = useState(false);
  const [isEditorFocus, setIsEditorFocus] = useState(false);
  const [resizedItemId, setResizedItemId] = useState(null);
  const [overSectionDirection, setOverSectionDirection] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);

  const queuesRef = useRef([]);
  const fullDocumentRef = useRefFn(() => fullDocument);
  const pathsRef = useRefFn(() => assignPath(fullDocumentRef.current));

  const handleError = ({ error, document }) => {
    if (!flags.flagBuilderWeightLimitation) {
      return;
    }

    const documentString = document ? JSON.stringify(document) : '';
    const documentLength = JSON.stringify(documentString).length;
    const isDocumentBig = documentLength >= 0.9 * WEIGHT_LIMITATION;
    const isDocumentTooBig = documentLength >= WEIGHT_LIMITATION;

    if (error) {
      return setError(ERRORS[error]);
    }

    if (
      documentString.includes('data:image') &&
      !documentString.includes('_imageData')
    ) {
      return setError(ERRORS.unsupported_images);
    }

    if (isDocumentTooBig) {
      return setError(ERRORS.document_too_big);
    }

    if (isDocumentBig) {
      return setError(ERRORS.document_almost_too_big);
    }

    return setError();
  };

  useEffect(() => {
    handleError({ document: fullDocument });
  }, []);

  const getIsMounted = useIsMounted();

  const handleChangeColumnWidth = ({
    acceptedValues,
    item,
    nbGridColumns,
    oldValue,
    position,
    value,
  }) =>
    handleUpdateItem({
      path: getPath(item.id),
      updater: (item) => {
        const sectionItem = getParentItem(item.id);
        const nbColumns = sectionItem.children.length;
        const nbSiblings = nbColumns - 1;

        const nbGridColumn = acceptedValues.indexOf(
          getClosestValue(acceptedValues, value),
        );

        // traverse the column in the opposite direction when the left handler is used
        const sectionChildren =
          position === 'right'
            ? sectionItem.children
            : [...sectionItem.children].reverse();
        const itemIndex = sectionChildren.findIndex(
          (child) => child.id === item.id,
        );

        let columnGridDiff;
        let isElementPast = false;
        let MAX_GRID_COLUMNS = nbGridColumns - nbSiblings;
        const MIN_GRID_COLUMNS = 1;

        sectionChildren.forEach((child, index) => {
          if (index === itemIndex) {
            const finalValueIndex =
              nbGridColumn > MAX_GRID_COLUMNS
                ? MAX_GRID_COLUMNS
                : nbGridColumn < MIN_GRID_COLUMNS
                  ? MIN_GRID_COLUMNS
                  : nbGridColumn;

            const finalValue = acceptedValues[finalValueIndex];

            columnGridDiff =
              finalValueIndex -
              acceptedValues.indexOf(getClosestValue(acceptedValues, oldValue));

            // set new width to resized column
            set(child, 'attributes.width', `${finalValue}%`);
            isElementPast = true;
          } else if (!isElementPast) {
            const nbGridColumns = acceptedValues.indexOf(
              getClosestValue(
                acceptedValues,
                parseFloat(child.attributes.width),
              ),
            );

            // calculate the space the resized column cannot take
            MAX_GRID_COLUMNS -= nbGridColumns - 1;
          } else if (isElementPast && columnGridDiff !== 0) {
            const nbGridColumns = acceptedValues.indexOf(
              getClosestValue(
                acceptedValues,
                parseFloat(child.attributes.width),
              ),
            );
            const columnsToSubtract =
              columnGridDiff >= nbGridColumns
                ? nbGridColumns -
                  (nbGridColumns - (nbGridColumns - MIN_GRID_COLUMNS))
                : columnGridDiff;

            columnGridDiff -= columnsToSubtract;

            const newWidth = acceptedValues[nbGridColumns - columnsToSubtract];

            set(child, 'attributes.width', `${newWidth}%`);
          }
        });

        return item;
      },
    });

  const handleChangeHeight = ({ item, value }) =>
    handleUpdateAttribute({
      id: item.id,
      attribute: 'height',
      value: Math.ceil(value),
    });

  const handleChangeSectionPadding = ({ item, value }) =>
    handleUpdateAttribute({
      id: item.id,
      attribute: 'containerPadding',
      value,
    });

  const handleAddItem = ({ path, id, item, direction }) =>
    handleUpdateItem({
      path: path || getParentPath(id),
      updater: (parentItem) => {
        let newIndex = 0;

        if (direction !== undefined) {
          newIndex = parentItem.children.findIndex((child) => child.id === id);

          newIndex += direction;
          newIndex = newIndex < 0 ? 0 : newIndex;
        }

        parentItem.children.splice(newIndex, 0, item);

        return parentItem;
      },
    });

  const handleRemoveItem = (id) =>
    handleUpdateItem({
      path: getParentPath(id),
      updater: (parentItem) => {
        const itemIndex = parentItem.children.findIndex(
          (child) => child.id === id,
        );

        parentItem.children.splice(itemIndex, 1);

        return parentItem;
      },
    });

  const setChildren = (itemId, children) => {
    handleUpdateItem({
      path: getPath(itemId),
      updater: (item) => {
        set(item, 'children', children);

        return item;
      },
    });
  };

  const handleUpdateAttribute = ({ id, attribute, value }) => {
    // attributes starting with `__` are not saved into the history stack
    const isTemporary = attribute.startsWith('__');

    handleUpdateItem({
      path: getPath(id),
      isTemporary,
      updater: (item) => {
        if (value === undefined) {
          delete item?.attributes?.[attribute];
        } else {
          set(item, `attributes.${attribute}`, value);
        }

        return item;
      },
    });
  };

  const handleUpdateAttributes = ({ id, values }) =>
    values.map(({ attribute, value }) =>
      handleUpdateAttribute({ id, attribute, value }),
    );

  const handleUpdateItem = ({ errorName, isTemporary, path, updater }) => {
    if (!isTemporary) {
      queuesRef.current.push({
        path,
        updater,
      });

      treatQueue(errorName);
    }
  };

  const handleUpdateItems = ({ path, updaters }) => {
    updaters.map((updater) => handleUpdateItem({ path, updater }));
  };

  const getParentPath = (id) => {
    const path = pathsRef.current[id];

    const parentPath = (path || []).slice(0, -2); // Remove the last 2 elements of path to get parent

    return parentPath;
  };

  const handleUpdateSetting = ({ errorName, id, settingKey, value }) => {
    handleUpdateItem({
      errorName,
      path: getPath(id),
      updater: (item) => {
        if (value === undefined) {
          const settingKeySplited = settingKey.split('.');

          if (settingKeySplited.length === 1) {
            delete item?.[settingKeySplited[0]];
          } else {
            delete item?.[settingKeySplited[0]]?.[settingKeySplited[1]];
          }
        } else {
          set(item, settingKey, value);
        }

        return item;
      },
    });
  };

  const handleUpdateSettings = ({ errorName, id, values }) =>
    values.map(({ settingKey, value }) =>
      handleUpdateSetting({ errorName, id, settingKey, value }),
    );

  const getPath = (id) => pathsRef.current[id];
  const getParentId = (id) =>
    get(fullDocumentRef.current, [...getParentPath(id), 'id']);

  const getSpecificParentByType = (id, searchedType) => {
    if (!id) {
      return null;
    }

    const parent = get(fullDocumentRef.current, [...getParentPath(id)]);

    if (!parent || parent?.type === BUILDER_DOCUMENT_GLOBAL_TYPE.ROOT) {
      return null;
    }

    if (parent?.type !== searchedType) {
      return getSpecificParentByType(parent.id, searchedType);
    }

    return parent;
  };

  const getBodyItem = () => getBodyItemInDocument(fullDocumentRef.current);

  const getLastSectionItem = () => {
    const body = getBodyItem();
    const lastSectionItem = findLast(
      body?.children || [],
      (child) => !UNDRAGGABLE_COMPONENTS.includes(child.type),
    );

    return lastSectionItem;
  };

  const getItem = (id) => get(fullDocumentRef.current, getPath(id));

  const getParentItem = (id) => get(fullDocumentRef.current, getParentPath(id));

  const getItemIndex = (id) =>
    getItem(getParentId(id)).children.findIndex((child) => child.id === id);

  const getBodyRealChildren = () => {
    const body = getBodyItem();

    return body.children.filter((section) => !section.attributes?.readOnly);
  };

  const isDocumentEmpty = !getBodyRealChildren().length;

  const treatQueue = debounce((errorName) => {
    const newFullDocument = queuesRef.current.reduce(
      (result, { path, updater }) => update(result, path, updater),
      fullDocumentRef.current,
    );

    queuesRef.current = [];
    setChangeCount((count) => count + 1);
    handleSetFullDocument(newFullDocument);
    handleError({ error: errorName, document: newFullDocument });
  }, 10);

  const handleSetFullDocument = (newFullDocument) => {
    if (getIsMounted()) {
      fullDocumentRef.current = { ...newFullDocument };
      pathsRef.current = assignPath(fullDocumentRef.current);

      setFullDocument(fullDocumentRef.current);
      onChange?.(fullDocumentRef.current);
    }
  };

  const handleResetSelectedItem = () => {
    setSelectedItemId(null);
  };

  useEffect(() => {
    // Reset selectedItemId if the item don't exist anymore (only when we update fullDocument)
    if (selectedItemId) {
      const selectedItemPath = pathsRef.current[selectedItemId];

      if (!selectedItemPath) {
        setSelectedItemId(null);
      }
    }
  }, [fullDocument]);

  const selectedItem = useMemo(
    () => (selectedItemId ? getItem(selectedItemId) : null),
    [selectedItemId, fullDocument],
  );

  const value = useMemo(
    () => ({
      changeCount,
      fullDocument,
      setFullDocument: handleSetFullDocument,
      overSectionDirection,

      getBodyItem,
      getItem,
      getItemIndex,
      getLastSectionItem,
      getParentId,
      getParentItem,
      getParentPath,
      getPath,
      getSpecificParentByType,

      isDocumentEmpty,
      isEditorFocus,
      setIsEditorFocus,

      error,
      handleError,

      resetSelectedItem: handleResetSelectedItem,
      selectedItem,

      changeSectionHeight: handleChangeHeight,
      changeSectionPadding: handleChangeSectionPadding,
      changeColumnWidth: handleChangeColumnWidth,
      isResizing,
      setIsResizing,
      setSelectedItemId,
      selectedItemId,
      resizedItemId,
      setResizedItemId,
      setOverSectionDirection,

      setChildren,

      addItem: handleAddItem,
      removeItem: handleRemoveItem,
      updateAttribute: handleUpdateAttribute,
      updateAttributes: handleUpdateAttributes,
      updateItem: handleUpdateItem,
      updateItems: handleUpdateItems,

      updateSetting: handleUpdateSetting,
      updateSettings: handleUpdateSettings,
    }),
    [
      error,
      fullDocument,
      handleError,
      isDocumentEmpty,
      isEditorFocus,
      isResizing,
      overSectionDirection,
      resizedItemId,
      selectedItem,
      selectedItemId,
    ],
  );

  return (
    <BuilderContext.Provider key={initialDocument} value={value}>
      {children}
    </BuilderContext.Provider>
  );
};

export const useBuilderContext = () => {
  const context = useContext(BuilderContext);

  if (!context) {
    throw new Error('useBuilderContext must be used within a BuilderContext');
  }

  return context;
};

export default BuilderContext;
